import { Controller } from "@hotwired/stimulus"

// Right-edge fade hint over a horizontally scrollable container: the fade is a
// separate overlay (not a CSS mask on the scroll container itself), because
// `mask-image` on a focusable element also clips its focus ring, making the
// keyboard focus outline appear/disappear depending on scroll position. Only
// shows the fade while there is more content to scroll to, and only makes the
// scroller a keyboard/AT stop (role, tabindex, accessible label) while it is
// actually scrollable, so the common case where everything fits leaves
// neither a pointless fade, an unlabeled group, nor a dead tab stop. The tab
// stop intentionally persists once added, even after scrolling all the way
// to the right — it only disappears when the content stops overflowing
// entirely.
//
// role/tabindex/aria-label are each only ever added when the scroller didn't
// already have that specific attribute; if a call site's markup already sets
// one, this controller never overwrites or removes it. Whatever it itself
// added is cleaned up both on disconnect and on `turbo:before-cache` (Turbo
// clones the page into its cache before this element's disconnect() callback
// runs, so disconnect() alone is too late to keep a stale attribute out of a
// Back/Forward-restored snapshot).
//
// Usage: mount `data-controller="scroll-fade"` on a wrapper, with
// `data-scroll-fade-target="scroller"` on the `overflow-x-auto` element and
// `data-scroll-fade-target="fade"` on a sibling overlay div (typically
// `absolute inset-y-0 right-0` with a `hidden` class toggled by this
// controller). See app/views/partners/reporting/_repricing_card.html.erb.
const ATTRIBUTES = {
  role: "group",
  tabindex: "0",
  "aria-label": "Scrollable content, use arrow keys to see more",
}
// A couple of px of tolerance absorbs fractional-zoom/DPR rounding, so a
// legend that visually fits doesn't get pinned with a 1-2px "more to scroll"
// state that can never be reached.
const SCROLL_TOLERANCE_PX = 2

export default class extends Controller {
  static targets = ["scroller", "fade"]

  connect() {
    this.update = this.update.bind(this)
    this.scrollerTarget.addEventListener("scroll", this.update, { passive: true })
    // Re-check on focus loss too: if the scroller stopped overflowing while it
    // was focused (see the guard in update()), attribute removal was deferred
    // to avoid yanking focus out from under the user; once focus actually
    // leaves, apply the deferred removal.
    this.scrollerTarget.addEventListener("focusout", this.update)
    // Observe the scroller itself (size changes) and its first child (content
    // width changes that don't affect the scroller's own box, e.g. a webfont
    // swapping in after this connects), so the fade stays accurate either way.
    this.resizeObserver = new ResizeObserver(this.update)
    this.resizeObserver.observe(this.scrollerTarget)
    if (this.scrollerTarget.firstElementChild) {
      this.resizeObserver.observe(this.scrollerTarget.firstElementChild)
    }
    // Only ever add an attribute when the markup didn't already have it;
    // only ever remove what this controller itself added.
    this.canSetAttribute = {}
    this.addedAttribute = {}
    for (const name of Object.keys(ATTRIBUTES)) {
      this.canSetAttribute[name] = !this.scrollerTarget.hasAttribute(name)
      this.addedAttribute[name] = false
    }
    // Turbo clones the page into its cache (`turbo:before-cache`) before this
    // element's own `disconnect()` fires on navigation, so `disconnect()`
    // alone is too late to keep a stale attribute out of the cached snapshot.
    // Clean up proactively here too, so Back/Forward restores from cache
    // don't resurrect an affordance this controller added.
    this.cleanUpForCache = this.cleanUpForCache.bind(this)
    document.addEventListener("turbo:before-cache", this.cleanUpForCache)
    this.update()
  }

  disconnect() {
    this.scrollerTarget.removeEventListener("scroll", this.update)
    this.scrollerTarget.removeEventListener("focusout", this.update)
    this.resizeObserver?.disconnect()
    document.removeEventListener("turbo:before-cache", this.cleanUpForCache)
    // Clean up whatever this controller added, so a fresh render of this
    // element (Turbo Stream replace, lazy frame reload, etc.) doesn't inherit
    // a stale affordance from a torn-down instance.
    this.cleanUpForCache()
  }

  cleanUpForCache() {
    for (const name of Object.keys(ATTRIBUTES)) {
      if (this.addedAttribute[name]) {
        this.scrollerTarget.removeAttribute(name)
        this.addedAttribute[name] = false
      }
    }
  }

  update() {
    const el = this.scrollerTarget
    const isScrollable = el.scrollWidth - el.clientWidth > SCROLL_TOLERANCE_PX
    const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft
    const hasMoreToScroll = remaining > SCROLL_TOLERANCE_PX

    this.fadeTarget.classList.toggle("hidden", !hasMoreToScroll)

    if (isScrollable) {
      for (const [name, value] of Object.entries(ATTRIBUTES)) {
        if (this.canSetAttribute[name]) {
          el.setAttribute(name, value)
          this.addedAttribute[name] = true
        }
      }
    } else if (document.activeElement !== el) {
      // Avoid removing attributes out from under a currently-focused element
      // (e.g. the viewport widens after the user tabbed in): that would blur
      // focus to document.body and reset tab order for no benefit to the
      // user. The focusout listener above re-runs update() once focus
      // actually leaves.
      for (const name of Object.keys(ATTRIBUTES)) {
        if (this.addedAttribute[name]) {
          el.removeAttribute(name)
          this.addedAttribute[name] = false
        }
      }
    }
  }
}
